import type { ShipmentTemplateWithTags } from '~api/userprofile';

import { Fragment, useRef } from 'react';

import { CellLayout, EmptyState, InteractiveTable, LoadingTableRows, MoreButton, Table } from '@ingrid/luterkort-react';

import { CarrierTransportProduct } from '~src/components/CarrierTransportProduct/CarrierTransportProduct';
import { Ellipsis } from '~src/components/Ellipsis/Ellipsis';
import { useTranslation } from '~src/i18n/use-translation';

import { ActionPanel } from './action-panel';
import { DetailsDrawer } from './details-drawer';
import { Menu } from './menu';

interface Props {
  shipmentTemplatesWithTags: ShipmentTemplateWithTags[];
  loadRowCount: number;
  isEmpty: boolean;
  loading: boolean;
}

export function ListTable(props: Props) {
  const { isEmpty, loadRowCount, loading, shipmentTemplatesWithTags } = props;

  const { highlightedKey } = InteractiveTable.useContext();
  const { t } = useTranslation();

  const selectedShipmentTemplateWithTags = shipmentTemplatesWithTags.find(c => c.template!.id === highlightedKey);
  const shipmentTemplates = shipmentTemplatesWithTags.map(templateWithTags => templateWithTags.template!);
  const tableRef = useRef<HTMLTableElement>(null);

  if (isEmpty) {
    return <EmptyState title={t('shipmentTemplates.list.noneFound')} />;
  }

  return (
    <Fragment>
      <InteractiveTable.Table ref={tableRef}>
        <thead>
          <Table.Row>
            <Table.HeadCell css={{ width: '30px' }}>
              <InteractiveTable.HeadCheckbox />
            </Table.HeadCell>
            <Table.HeadCell css={{ width: '45%' }}>{t('common.name')}</Table.HeadCell>
            <Table.HeadCell css={{ width: '45%' }}>{t('common.carrierProduct')}</Table.HeadCell>
            <Table.HeadCell css={{ width: '35px' }} />
          </Table.Row>
        </thead>
        <tbody>
          {shipmentTemplates.map(template => (
            <InteractiveTable.Row key={template.id} value={template.id!}>
              <Table.Cell>
                <InteractiveTable.Checkbox value={template.id!} />
              </Table.Cell>
              <Table.Cell>
                <Ellipsis>{template.data?.name}</Ellipsis>
              </Table.Cell>
              <Table.Cell>
                {template.data?.shipping_method && (
                  <CellLayout.Row>
                    <CarrierTransportProduct
                      queryOptions={{ onlyConfigured: false }}
                      methodId={template.data.shipping_method}
                      width={32}
                    />
                  </CellLayout.Row>
                )}
              </Table.Cell>
              <Table.Cell>
                <Menu template={template} trigger={<MoreButton size="sm" />} displayEditAction />
              </Table.Cell>
            </InteractiveTable.Row>
          ))}
          {loading && <LoadingTableRows tableRef={tableRef} rowCount={loadRowCount} />}
        </tbody>
      </InteractiveTable.Table>

      <ActionPanel />

      <DetailsDrawer shipmentTemplateWithTags={selectedShipmentTemplateWithTags} />
    </Fragment>
  );
}
